import { html, LitElement, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { AtlDataTableProps, DataTableCell, DataTableColumn } from "./data-table.props.ts";
import { ifDefined } from "lit/directives/if-defined.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { ButtonEnums } from "../button/index.ts";
import { IconSizes } from "../icon/icon.props.ts";
import { getGlobalStyleSheets } from "../../global-styles";

import * as TagNames from './data-table.tagnames.ts';

@customElement(TagNames.DataTable)
export class AtlDataTable extends LitElement implements AtlDataTableProps {
    declare columns: DataTableColumn[];
    declare rows: DataTableCell[][];
    
    static properties = {
        columns: { type: Array },
        rows: { type: Array }
    };

    static styles = [
        ...getGlobalStyleSheets()
    ];

    constructor() {
        super();
        this.columns = [];
        this.rows = [][0];
    }

    private _renderTableCell(column: DataTableColumn) {
        const cellContent = typeof column.content === 'string' ? unsafeHTML(column.content) : column.content;

        const buttonContent = column.styledHead
            ? html`<span class="btn btn-link text-truncate text-start">${cellContent}</span>`
            : html`${cellContent}`;
        
        if (!column.sortable) return buttonContent;
        
        return html`
            <atlantis-button variant=${ButtonEnums.ButtonVariant.Action} class="text-truncate text-start">
                ${html`${buttonContent}`}
                ${column.sortDirection ? html`<atlantis-icon size=${IconSizes.Small} name=${column.sortDirection === 'asc' ? "chevron-up" : "chevron-down"}></atlantis-icon>` : nothing}                
            </atlantis-button>
        `;
    }

    private _renderTableHeader() {
        return html`
            <thead>
                <tr>
                    ${this.columns.map(column => 
                        html`<th scope="col" width=${ifDefined(column.width)}>
                            ${this._renderTableCell(column)}                      
                        </th>`
                    )}
                </tr>
            </thead>
        `;
    }

    private _renderCell(cell: DataTableCell) {
        const content = typeof cell.content === 'string' ? unsafeHTML(cell.content) : cell.content;        
        return cell.scopeRow 
            ? html`<th 
                scope="row" 
                width=${ifDefined(cell.width)} 
                colspan=${ifDefined(cell.colspan)}
            >${content}</th>`
            : html`<td
                width=${ifDefined(cell.width)} 
                colspan=${ifDefined(cell.colspan)}
            >${content}</td>`;
    }

    private _renderRow(row: DataTableCell[]) {
        return html`
            <tr>
                ${row.map(cell => this._renderCell(cell) )}
            </tr>
        `;
    }

    private _renderTableBody() {
        return html`
            <tbody>
                ${html`${this.rows.map(row => html`${this._renderRow(row)}`)}`}
            </tbody>
        `;
    }

    render() {
        return html`
            <div class="datatable-container">
                <slot name="filters"></slot>
                <div class="table-responsive datatable-responsive">
                    <table class="table datatable">
                        ${this._renderTableHeader()}
                        ${this._renderTableBody()}
                    </table>
                </div>
                <slot name="pagination"></slot>
            </div>    
        `;
    }
}